import classNames from 'classnames'
import { getIn, useFormikContext } from 'formik'
import { TFunction } from 'i18next'
import React, { type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { W8BeneficialFormValues } from '@pages/Profile/W8Beneficial/W8BeneficialForm'

import { useModal } from '@context/ModalsContext'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Checkbox } from '@ui/components/Checkbox'
import { Link } from '@ui/components/Link'
import { Radio } from '@ui/components/Radio'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { EnglishLettersPattern } from '@utils/formValidation'

import { DocumentCategorySizeDto } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'
import {
  AnswerGroupOption,
  AnswerMessageLevel,
  QuestionDto,
  TestQuestionWidgetType,
  TestSectionAnswerDto,
  TestSectionDto,
  TestSectionQuestionDto,
  TestSectionWidgetDto,
  WidgetOptions,
} from '@model/TestSectionsDto'

import { createFormField } from '@global/formField/FormField'
import { RawSelectFormField } from '@global/formField/FormSelectField'
import { createFormRadioField } from '@global/formField/RadioGroupField'

import { useFetchRegulators } from '@hooks/useFetchRegulators'

import { Box } from '@shared/components/Box'
import { DocumentUploadModal, FormValuesWithDocuments } from '@shared/modals/DocumentUpload.modal'

import styles from './TestFields.module.scss'

const sortByOrder = <T extends { order: number }>(items: readonly T[] | T[] = []): T[] =>
  [...items].sort((a, b) => a.order - b.order)

const FormRadioGroupField = createFormRadioField()
const FormField = createFormField()

interface CommonWidgetProps extends TestSectionQuestionDto {
  section: TestSectionDto
  allQuestions?: TestSectionQuestionDto[]
  categorySizes?: DocumentCategorySizeDto[]
  documents?: DocumentClientAreaDto[]
}

interface SectionOnlyProps {
  section: TestSectionDto
  allQuestions?: TestSectionQuestionDto[]
  categorySizes?: DocumentCategorySizeDto[]
  documents?: DocumentClientAreaDto[]
  question?: never
  widget?: never
}

interface DocumentUploadLinkProps<T> {
  label: string
  values: T
  setValues: (values: T) => void
  documents?: DocumentClientAreaDto[]
  questionId: string
  categorySizes?: DocumentCategorySizeDto[]
}

type SelectedAnswersMap = Record<string, string | string[]>
type SelectedAnswersInput =
  | Record<string, any>
  | string[]
  | Array<{ answer?: string | null; [key: string]: any }>

const getAllQuestions = (sections: readonly TestSectionDto[] = []): TestSectionQuestionDto[] =>
  sections.flatMap((section) => section.questions)

const getSelectedAnswerValues = (selectedAnswers?: SelectedAnswersInput): string[] => {
  if (!selectedAnswers) return []

  if (Array.isArray(selectedAnswers)) {
    return selectedAnswers
      .flatMap((value) => {
        if (typeof value === 'string') return value

        if (value && typeof value === 'object' && 'answer' in value) {
          return value.answer ? [value.answer] : []
        }

        return []
      })
      .filter(Boolean)
  }

  return Object.values(selectedAnswers)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
}

const dedupeValues = (values: string[]): string[] =>
  values.filter((value, index) => values.indexOf(value) === index)

const latinTextPatternPrefix = EnglishLettersPattern.source.replace(/\+\$$/, '')

export const getRegExpValidationError = (
  value: string,
  pattern: string,
  t: TFunction
): string | undefined => {
  if (new RegExp(pattern).test(value)) return undefined

  const limits = /\{(\d+),(\d+)\}\$$/.exec(pattern)

  if (limits) {
    const min = Number(limits[1])
    const max = Number(limits[2])

    if (value.length > max) {
      return t('Validation.Max {{amount}} characters allowed', { amount: max })
    }

    if (value.length < min) {
      return t('Validation.Min {{amount}} characters required', { amount: min })
    }
  }

  return pattern.startsWith(latinTextPatternPrefix)
    ? t('Validation.Please use Latin characters only')
    : t('Validation.Invalid format')
}
const getAnswerGroupOverlap = (currentAnswers: string[], answerGroup?: AnswerGroupOption): number =>
  currentAnswers.filter((answerId) => answerGroup?.values?.includes(answerId)).length

export const getNextMultiSelectAnswers = (
  currentAnswers: string[],
  answerId: string,
  answerGroups?: AnswerGroupOption[]
): string[] => {
  if (currentAnswers.includes(answerId)) {
    return currentAnswers.filter((selectedAnswer) => selectedAnswer !== answerId)
  }

  if (!answerGroups?.length) {
    return dedupeValues([...currentAnswers, answerId])
  }

  const currentGroups = answerGroups.filter((answerGroup) =>
    currentAnswers.every((selectedAnswer) => answerGroup.values?.includes(selectedAnswer))
  )

  if (currentGroups.some((answerGroup) => answerGroup.values?.includes(answerId))) {
    return dedupeValues([...currentAnswers, answerId])
  }

  const nextGroup = [...answerGroups]
    .filter((answerGroup) => answerGroup.values?.includes(answerId))
    .sort(
      (leftGroup, rightGroup) =>
        getAnswerGroupOverlap(currentAnswers, rightGroup) -
        getAnswerGroupOverlap(currentAnswers, leftGroup)
    )[0]

  if (!nextGroup?.values?.length) {
    return dedupeValues([...currentAnswers, answerId])
  }

  return dedupeValues([
    ...currentAnswers.filter((selectedAnswer) => nextGroup.values?.includes(selectedAnswer)),
    answerId,
  ])
}

export const QuestionFieldFactory = <T,>(
  props: CommonWidgetProps | SectionOnlyProps
): JSX.Element | null => {
  if (!('question' in props) || !props.question) {
    const { section, allQuestions, categorySizes, documents } = props
    if (!section?.questions) return null

    return (
      <div className={styles.testWidget}>
        {sortByOrder(section.questions).map((q) => (
          <SingleQuestionField<T>
            key={q.question.id}
            {...q}
            section={section}
            allQuestions={allQuestions}
            categorySizes={categorySizes}
            documents={documents}
          />
        ))}
      </div>
    )
  }

  return <SingleQuestionField<T> {...(props as CommonWidgetProps)} />
}

const SingleQuestionField = <T,>(props: CommonWidgetProps): JSX.Element | null => {
  const { section, widget, allQuestions } = props
  const context = useFormikContext<T>()

  const values = context.values as any

  const isVisible = showConditionalQuestions(
    allQuestions || section?.questions || [],
    values['selectedAnswers'],
    widget
  )

  if (isOptionsHide(widget.widgetOptions) && !isVisible) {
    return null
  }

  switch (props.question.widgetType) {
    case TestQuestionWidgetType.Select:
      return <SelectWidget<T> {...props} />
    case TestQuestionWidgetType.Radio:
      return <RadioWidget<T> {...props} />
    case TestQuestionWidgetType.Country:
      return <CountryWidget {...props} />

    case TestQuestionWidgetType.Checkbox:
      return <CheckboxWidget<T> {...props} />
    case TestQuestionWidgetType.Multiselect:
      return <MultiSelectWidget<T> {...props} />
    default:
      return <TextWidget<T> {...props} />
  }
}

const DocumentUploadLink = <T,>({
  label,
  values,
  setValues,
  documents,
  questionId,
  categorySizes,
}: DocumentUploadLinkProps<T>) => {
  const documentUploadModal = useModal(DocumentUploadModal)

  return (
    <span
      onClick={() =>
        documentUploadModal.open({
          values: values as unknown as FormValuesWithDocuments,
          setValues: setValues as any,
          apiDocuments: documents,
          questionId,
          categorySizes,
        })
      }
      className={styles.documentUploadLink}
    >
      {label}
    </span>
  )
}

const SelectWidget = <T,>(props: CommonWidgetProps) => {
  const { t } = useTranslation()
  const context = useFormikContext<T>()

  const { question, answers, widget, categorySizes, documents } = props

  if (!widget?.name || !question?.name) return null

  const prefix = 'selectedAnswers.'
  const fieldName = prefix + widget.name

  const options = widget.widgetOptions
  const isMandatory = options?.isMandatory || props.isMandatory

  const value = getIn(context.values, fieldName)

  const selectedAnswer = answers.find((answer) => answer.id === value)
  const isDocumentUploadAllowed =
    !!selectedAnswer && (options?.upload?.values?.includes(selectedAnswer.id) ?? false)

  const documentUploadLabel =
    selectedAnswer?.onSelectMessage?.message ||
    t(
      'Sign up.If available please upload any documentary evidence of trading history e.g. trading statement (Optional)'
    )

  const selectOptions = sortByOrder(answers).map((x) => ({ id: x.id, name: x.name }))

  return (
    <div className={styles.questionAnswerField}>
      <QuestionName question={question} widget={widget} isMandatory={isMandatory} />
      <RawSelectFormField
        namePrefix={prefix}
        name={widget.name}
        options={selectOptions}
        required={isMandatory}
        showLabel={false}
        label={t('Select')}
        value={value}
        wrapText
      />
      <AnswerMessages
        answers={answers}
        value={value}
        shouldHide={isDocumentUploadAllowed && !!selectedAnswer?.onSelectMessage?.message}
      />
      {isDocumentUploadAllowed && (
        <DocumentUploadLink
          label={documentUploadLabel}
          values={context.values}
          setValues={context.setValues}
          documents={documents}
          questionId={question.questionId}
          categorySizes={categorySizes}
        />
      )}
    </div>
  )
}

const RadioWidget = <T,>(props: CommonWidgetProps) => {
  const { t } = useTranslation()
  const context = useFormikContext<T>()

  const { question, answers, widget, categorySizes, documents } = props

  if (!widget?.name || !question?.name) return null

  const name = `selectedAnswers.${widget.name}`
  const options = widget.widgetOptions
  const isMandatory = options?.isMandatory || props.isMandatory
  const value = getIn(context.values, name)
  const error = getIn(context.errors, widget.name)
  const touched = getIn(context.touched, name)

  const isDocumentUploadAllowed = isRadioWidgetCode(options, answers, value)

  const selectedAnswer = answers.find((answer) => answer.id === value)

  const documentUploadLabel =
    selectedAnswer?.onSelectMessage?.message ||
    t(
      'Sign up.If available please upload any documentary evidence of trading history e.g. trading statement (Optional)'
    )

  return (
    <div className={classNames(styles.questionAnswerField)}>
      <QuestionName question={props.question} widget={props.widget} isMandatory={isMandatory} />
      <FormRadioGroupField
        name={name}
        value={value}
        required
        error={touched ? error : undefined}
        validate={(val: any) =>
          isMandatory && !val ? question?.errorMessage || t('Validation.Required') : undefined
        }
      >
        {sortByOrder(answers).map((x) => (
          <Radio
            name={name}
            value={x.id}
            checked={x.id === value}
            key={x.id}
            onChange={() => {
              context.setFieldValue(name, x.id)
              context.setFieldTouched(name, true)
            }}
          >
            {x.name}
          </Radio>
        ))}
      </FormRadioGroupField>
      {isDocumentUploadAllowed && (
        <DocumentUploadLink
          label={documentUploadLabel}
          values={context.values}
          setValues={context.setValues}
          documents={documents}
          questionId={question.questionId}
          categorySizes={categorySizes}
        />
      )}
    </div>
  )
}

const MultiSelectWidget = <T,>(props: CommonWidgetProps) => {
  const { question, answers, widget } = props
  const context = useFormikContext<T>()
  const values = context.values as { selectedAnswers: any; [key: string]: any }

  if (!widget?.name || !question?.name) return null

  const options = widget.widgetOptions
  const isMandatory = options?.isMandatory || props.isMandatory

  const error = getIn(context.errors, widget.name)
  const touched = getIn(context.touched, `selectedAnswers.${widget.name}`)

  const handleMultiSelect = (answerId: string) => {
    if (!touched) context.setFieldTouched(`selectedAnswers.${widget.name}`, true)

    const currentAnswers = values.selectedAnswers[widget.name] || []
    const newAnswers = getNextMultiSelectAnswers(currentAnswers, answerId, options?.answerGroups)

    context.setFieldValue('selectedAnswers', {
      ...values.selectedAnswers,
      [widget.name]: newAnswers,
    })
  }

  return (
    <div className={classNames(styles.questionAnswerField)}>
      <QuestionName question={props.question} widget={props.widget} isMandatory={isMandatory} />
      {sortByOrder(answers).map((answer) => (
        <div key={answer.id}>
          <Checkbox
            onChange={() => handleMultiSelect(answer.id)}
            checked={values.selectedAnswers[widget.name]?.includes(answer.id)}
          >
            <span>{answer.name}</span>
          </Checkbox>
        </div>
      ))}
      {touched && error && (
        <Caption1 as='div' color='text-danger-high-em'>
          {error}
        </Caption1>
      )}
    </div>
  )
}

const CheckboxWidget = <T,>(props: CommonWidgetProps) => {
  const { question, widget } = props
  const context = useFormikContext<T>()

  if (!widget?.name || !question?.name) return null
  const name = `freeAnswers.${question.id}`
  const options = widget.widgetOptions
  const isMandatory = options?.isMandatory || props.isMandatory
  const values = context.values as { selectedAnswers: any; [key: string]: any }
  const error = getIn(context.errors, widget.name)
  const touched = getIn(context.touched, name)
  const { tooltip, infoCard } = widget
  const hasInfoCard = infoCard && (infoCard.text || infoCard?.text)

  const handleChange = (checked: boolean) => {
    if (!touched) context.setFieldTouched(name, true)

    context.setFieldValue('freeAnswers', {
      ...values.freeAnswers,
      [question.id]: checked ? '1' : '0',
    })
  }

  return (
    <Box flex gap='x4' direction='column'>
      <Box flex align='baseline' gap='x4'>
        <Checkbox error={touched ? error : undefined} name={name} onChange={handleChange}>
          <Box flex minWidth={0} align='center' gap='x4'>
            <Body1>
              {question.name}
              {isMandatory ? <span>*</span> : null}
            </Body1>
            {tooltip && !hasInfoCard && (
              <Tooltip content={tooltip} autoHide={2000}>
                <ActionIcon icon={InfoIcon} size='x20' />
              </Tooltip>
            )}
            {infoCard && hasInfoCard && (
              <Tooltip
                heading={infoCard.title}
                content={
                  <>
                    {infoCard.text}
                    {infoCard.link && (
                      <Body1>
                        <Link to={infoCard.link.to}>{infoCard.link.text}</Link>
                      </Body1>
                    )}
                  </>
                }
              >
                <ActionIcon icon={InfoIcon} size='x20' />
              </Tooltip>
            )}
          </Box>
        </Checkbox>
      </Box>
      {question.text !== question.name && <Body1>{question.text}</Body1>}
    </Box>
  )
}

const TextWidget = <T,>(props: CommonWidgetProps) => {
  const { question, widget } = props
  const context = useFormikContext<T>()
  const { t } = useTranslation()

  if (!widget?.name || !question?.name) return null

  const options = widget?.widgetOptions
  const name = `freeAnswers.${question.id}`
  const isMandatory = options?.isMandatory || props.isMandatory
  const error = getIn(context.errors, widget.name)
  const touched = getIn(context.touched, name)
  const value = getIn(context.values, name)

  return (
    <div className={styles.questionAnswerField}>
      <QuestionName question={props.question} widget={props.widget} isMandatory={isMandatory} />
      <FormField
        name={name}
        required={isMandatory}
        showLabel={false}
        error={
          (touched && error) ||
          (isMandatory && !value && touched
            ? question?.errorMessage || t('Validation.Required')
            : undefined)
        }
        validate={(val: any) => {
          if (isMandatory && (!val || (typeof val === 'string' && val.trim() === ''))) {
            return question?.errorMessage || t('Validation.Required')
          }
          if (options?.regExp && val) {
            return getRegExpValidationError(String(val), options.regExp, t)
          }
          return undefined
        }}
      />
    </div>
  )
}

const CountryWidget = (props: CommonWidgetProps) => {
  const { t } = useTranslation()
  const regulators = useFetchRegulators()

  if (!props.widget?.name || !props.question?.name) return null

  const name = `freeAnswers.${props.question.id}`
  const options = props.widget.widgetOptions

  const isMandatory = options?.isMandatory || props.isMandatory

  const countryOptions = regulators?.data?.map((regulator) => ({
    id: regulator.country.id,
    name: regulator.country.name,
  }))

  return (
    <div className={styles.questionAnswerField}>
      <QuestionName question={props.question} widget={props.widget} isMandatory={isMandatory} />
      <RawSelectFormField
        name={name}
        required={isMandatory}
        searchable
        options={countryOptions || []}
        hasCheckMark={false}
        label={t('Select Country')}
        showLabel={false}
      />
    </div>
  )
}

export const validateTestForm = (
  sections: readonly TestSectionDto[],
  values:
    | W8BeneficialFormValues
    | {
        selectedAnswers?: Record<string, any>
        freeAnswers?: Record<string, string>
      },
  t: TFunction,
  allSections: readonly TestSectionDto[] = sections
) => {
  const errors: Record<string, string> = {}
  const allQuestions = getAllQuestions(allSections)

  sections.forEach((section) => {
    section.questions
      .filter(({ widget }) =>
        showConditionalQuestions(allQuestions, values.selectedAnswers, widget)
      )
      .forEach((question) => {
        const options = question.widget.widgetOptions
        const widgetName = question.widget.name
        const isMandatory = options?.isMandatory || question.isMandatory
        let selectedVal
        let freeVal

        if (isW8BeneficialFormValues(values)) {
          const fieldValue = values[widgetName]

          selectedVal =
            fieldValue ??
            question.answers?.find((answer) => values.selectedAnswers.includes(answer.id))?.id

          freeVal = values.freeAnswers?.find(
            (answer) => answer.testQuestionId === question.question.id
          )?.answer
        } else {
          selectedVal = values.selectedAnswers?.[widgetName]
          freeVal = values.freeAnswers?.[question.question.id]
        }

        const hasAnswer =
          question.widget.type === TestQuestionWidgetType.Checkbox
            ? freeVal
              ? freeVal === '1'
              : selectedVal
            : (selectedVal &&
                (Array.isArray(selectedVal) ? selectedVal.length > 0 : !!selectedVal)) ||
              !!freeVal

        if (isMandatory && !hasAnswer) {
          errors[widgetName] = question.widget.errorMessage || t('Validation.Required')
        } else if (options?.regExp && freeVal) {
          const regExpError = getRegExpValidationError(String(freeVal), options.regExp, t)
          if (regExpError) {
            errors[widgetName] = regExpError
          }
        }
      })
  })
  return errors
}

export const getCleanedAnswers = (
  selectedAnswers: SelectedAnswersMap,
  freeAnswers: Record<string, string>,
  sections: readonly TestSectionDto[]
): { selectedAnswers: SelectedAnswersMap; freeAnswers: Record<string, string> } => {
  const allQuestions = getAllQuestions(sections)
  const cleanedSelectedAnswers: SelectedAnswersMap = {}
  const cleanedFreeAnswers: Record<string, string> = {}

  sections.forEach((section) => {
    section.questions.forEach((q) => {
      if (showConditionalQuestions(allQuestions, selectedAnswers, q.widget)) {
        const widgetName = q.widget.name
        const questionId = q.question.id
        const val = selectedAnswers[widgetName]

        if (Array.isArray(val)) {
          if (val.length > 0) cleanedSelectedAnswers[widgetName] = val
        } else if (val) {
          cleanedSelectedAnswers[widgetName] = val
        }

        if (freeAnswers[questionId]) cleanedFreeAnswers[questionId] = freeAnswers[questionId]
      }
    })
  })
  return { selectedAnswers: cleanedSelectedAnswers, freeAnswers: cleanedFreeAnswers }
}

export const showConditionalQuestions = (
  questions: readonly TestSectionQuestionDto[],
  selectedAnswers?: SelectedAnswersInput,
  widget?: TestSectionWidgetDto,
  visited?: Set<string>
): boolean => {
  const parsedAnswers = getSelectedAnswerValues(selectedAnswers)

  const dependsOn = questions.find((q) => q.widget.name === widget?.widgetOptions?.depend)

  if (!dependsOn) return true

  const seen = visited ?? new Set<string>()

  const currentName = widget?.name
  if (currentName && seen.has(currentName)) return false
  if (currentName) seen.add(currentName)

  const isParentVisible = showConditionalQuestions(
    questions,
    selectedAnswers,
    dependsOn.widget,
    seen
  )
  if (!isParentVisible) return false

  const answersNeededToShow: string[] = []
  dependsOn.widget.widgetOptions?.onChange
    ?.filter((change) => change.name === widget?.name)
    ?.forEach((onchange) => {
      if (onchange.values) answersNeededToShow.push(...onchange.values)
    })

  return parsedAnswers?.some((answer) => answersNeededToShow?.includes(answer))
}

interface QuestionNameProps {
  question: QuestionDto
  widget: TestSectionWidgetDto
  isMandatory: boolean
}

const getMessageLevel = (typeId: number): AnswerMessageLevel => {
  switch (typeId) {
    case 1:
      return 'Info'
    case 2:
      return 'Warning'
    case 3:
      return 'Critical'
    default:
      return 'Critical'
  }
}

const getTextColorClass = (level: AnswerMessageLevel) => {
  switch (level) {
    case 'Info':
      return 'text-info-med-em'
    case 'Warning':
      return 'text-warning-med-em'
    case 'Critical':
      return 'text-danger-med-em'
    default:
      return 'text-danger-med-em'
  }
}

const AnswerMessages: React.FC<{
  answers: TestSectionAnswerDto[]
  value: string
  shouldHide?: boolean
}> = ({ answers, value, shouldHide }) => {
  if (shouldHide) return null

  const selectedAnswer = answers.find((a) => a.id === value)
  const onSelectMessage = selectedAnswer?.onSelectMessage

  if (!onSelectMessage) return null

  const messageLevel = getMessageLevel(onSelectMessage.typeId)
  const textColorClass = getTextColorClass(messageLevel)
  return <Body2 className={textColorClass}>{onSelectMessage.message}</Body2>
}

const QuestionName: React.FC<QuestionNameProps> = ({ question, widget, isMandatory }) => {
  const { tooltip, infoCard } = widget
  const hasInfoCard = infoCard && (infoCard.text || infoCard?.text)

  return (
    <Box flex direction='column' gap='x12'>
      <Box flex align='center' minWidth={0} gap='x4'>
        <Box flex={1}>
          <Body1>
            {question.name}
            {isMandatory ? <span>*</span> : null}
          </Body1>
        </Box>
        {tooltip && !hasInfoCard && (
          <Tooltip content={tooltip} autoHide={2000}>
            <ActionIcon icon={InfoIcon} size='x20' />
          </Tooltip>
        )}
        {infoCard && hasInfoCard && (
          <Tooltip
            heading={infoCard.title}
            content={
              <>
                {infoCard.text}
                {infoCard.link && (
                  <Body1>
                    <Link to={infoCard.link.to}>{infoCard.link.text}</Link>
                  </Body1>
                )}
              </>
            }
          >
            <ActionIcon icon={InfoIcon} size='x20' />
          </Tooltip>
        )}
      </Box>
      {question.text !== question.name && <Body1>{question.text}</Body1>}
    </Box>
  )
}

const isOptionsHide = (options: WidgetOptions | null) =>
  options && 'hide' in options && 'depend' in options && options?.hide === true

const isRadioWidgetCode = (
  options: WidgetOptions | null,
  answers: TestSectionAnswerDto[],
  value: string
) => {
  const code = getRadioWidgetCode(answers, value)
  return (options?.upload?.values || []).map((x) => parseInt(x.toString())).includes(code)
}

const getRadioWidgetCode = (answers: TestSectionAnswerDto[], value: string) => {
  const answer = answers.find((x) => x?.id === value)
  return answer ? parseInt(answer.code, 10) : -1
}

const isW8BeneficialFormValues = (
  values:
    | W8BeneficialFormValues
    | {
        selectedAnswers?: Record<string, any>
        freeAnswers?: Record<string, string>
      }
): values is W8BeneficialFormValues => {
  return Array.isArray(values.selectedAnswers)
}
